import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { robustFetch } from './utils/apiUtils';
import SocialLinks from "./components/SocialLinks";
import { API_ENDPOINTS } from './config/api';
import { getMainSiteUrl, getBlogUrl, getPodcastUrl, getStoreUrl, getAppsUrl } from "./utils/subdomain";
import { useRegion } from './context/RegionContext';
import { useLanguage } from './context/LanguageContext';
import FooterLocationModal, { getCountryFlag } from './components/FooterLocationModal';
import FooterLanguageModal from './components/FooterLanguageModal';

const APPS = [
    { id: 'pomodoro', name: 'Pomodoro', path: '/app-store/pomodoro' },
    { id: 'typewriting', name: 'Typewriting', path: '/app-store/typewriting' },
    { id: 'reader', name: 'Reader', path: '/app-store/reader' },
];

export default function Footer(){
    const navigate = useNavigate();
    const [blogCategories, setBlogCategories] = useState([]);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

    const { selectedCountry, regions } = useRegion();
    const { currentLanguage, t } = useLanguage();

    const activeRegion = regions?.find((r: any) => (r.code || r.countryCode) === selectedCountry);
    const currentFlag = getCountryFlag(selectedCountry || 'IN');
    const currentRegionName = activeRegion?.name || (selectedCountry === 'IN' ? 'India' : (selectedCountry || 'India'));
    const currentCurrencyCode = activeRegion?.currency || (selectedCountry === 'IN' ? 'INR' : 'USD');
    const currentCurrencySymbol = activeRegion?.currencySymbol || (selectedCountry === 'IN' ? '₹' : '$');

    useEffect(() => {
        // Fetch Blog Categories
        robustFetch(API_ENDPOINTS.BLOG.CATEGORIES)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setBlogCategories(data);
                else if (data && data.success && Array.isArray(data.data)) setBlogCategories(data.data);
            })
            .catch(() => {});
    }, []);

    const handleBlogCategoryClick = (category: any) => {
        window.location.href = getBlogUrl(`/category/${category.slug || category.name?.toLowerCase().replace(/\s+/g, '-')}`);
    };

    return(
        <div className="global-footer flex w-95 m-2 flex-col items-center justify-center overflow-hidden bg-hover rounded-[25px] border-[5px] border-gray-666">
            <div className="flex w-full items-start gap-16 px-8 py-12 lg:gap-12 lg:px-8 lg:py-10 max-[900px]:flex-col max-[900px]:gap-12 max-[900px]:px-6 max-[900px]:py-8 xs:px-6 xs:py-8">
                <div className="flex-1 flex flex-col gap-4">
                    <div className="w-full flex items-center gap-[15px]">
                        <a href={getMainSiteUrl()}>
                            <img className="w-[50px] h-[50px] object-contain rounded-[12px] border-[1.5px] border-gray-666 transition-all duration-300" src="/images/home_images/Logo_Transparent.png" alt="Hari Vishnu Logo"/>
                        </a>
                        <h3 className="text-4xl font-extrabold text-text m-0">Hari Vishnu</h3>
                    </div>
                    <SocialLinks noShadow />
                    <a href="#Top" className="flex items-center gap-[5px] w-fit mt-[15px] no-underline text-text text-[0.95rem] font-semibold opacity-80 transition-all duration-300 hover:opacity-100 hover:text-primary-cyan hover:-translate-y-0.5" title="Back to Top">
                        <span>{t('footer.goToTop', 'Go to Top')}</span>
                        <i className="fi fi-rr-arrow-small-up text-[1.2rem] flex items-center"></i>
                    </a>
                </div>

                <div className="flex-1 grid grid-cols-4 gap-2 lg:grid-cols-4 max-[640px]:w-full max-[640px]:grid-cols-2 max-[640px]:gap-y-8">
                    <div className="flex flex-col">
                        <a href={getBlogUrl()} className="no-underline">
                            <h3 className="text-[1.1rem] font-bold text-primary-cyan mb-2 cursor-pointer uppercase tracking-wider transition-colors duration-300 hover:text-text">
                                {t('footer.blog', 'Blog')}
                            </h3>
                        </a>
                        <ul className="list-none p-0 m-0 flex flex-col gap-[0.7rem]">
                            {blogCategories.length > 0 ? blogCategories.slice(0, 5).map((cat: any) => (
                                <a key={cat._id} href={getBlogUrl(`/category/${cat.slug || cat.name?.toLowerCase().replace(/\s+/g, '-')}`)} className="no-underline">
                                    <li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">
                                        {cat.name}
                                    </li>
                                </a>
                            )) : (
                                <>
                                    <a href={getBlogUrl('/category/technology')} className="no-underline"><li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">{t('footer.technology', 'Technology')}</li></a>
                                    <a href={getBlogUrl('/category/lifestyle')} className="no-underline"><li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">{t('footer.lifestyle', 'Lifestyle')}</li></a>
                                </>
                            )}
                            <a href={getBlogUrl()} className="no-underline">
                                <li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">
                                    {t('footer.viewAll', 'View All')}
                                </li>
                            </a>
                        </ul>
                    </div>

                    <div className="flex flex-col">
                        <a href={getPodcastUrl()} className="no-underline">
                            <h3 className="text-[1.1rem] font-bold text-primary-cyan mb-2 cursor-pointer uppercase tracking-wider transition-colors duration-300 hover:text-text">
                                {t('footer.podcast', 'Podcast')}
                            </h3>
                        </a>
                        <ul className="list-none p-0 m-0 flex flex-col gap-[0.7rem]">
                            <a href={getPodcastUrl()} className="no-underline">
                                <li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">
                                    {t('footer.allEpisodes', 'All Episodes')}
                                </li>
                            </a>
                        </ul>
                    </div>

                    <div className="flex flex-col">
                        <a href={getStoreUrl()} className="no-underline">
                            <h3 className="text-[1.1rem] font-bold text-primary-cyan mb-2 cursor-pointer uppercase tracking-wider transition-colors duration-300 hover:text-text">
                                {t('footer.shop', 'Shop')}
                            </h3>
                        </a>
                        <ul className="list-none p-0 m-0 flex flex-col gap-[0.7rem]">
                            <a href={getStoreUrl()} className="no-underline">
                                <li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">
                                    {t('footer.viewAll', 'View All')}
                                </li>
                            </a>
                        </ul>
                    </div>

                    <div className="flex flex-col">
                        <a href={getAppsUrl()} className="no-underline">
                            <h3 className="text-[1.1rem] font-bold text-primary-cyan mb-2 cursor-pointer uppercase tracking-wider transition-colors duration-300 hover:text-text">
                                {t('footer.appStore', 'App Store')}
                            </h3>
                        </a>
                        <ul className="list-none p-0 m-0 flex flex-col gap-[0.7rem]">
                            {APPS.map(app => (
                                <a key={app.id} href={getAppsUrl(app.path.replace(/^\/app-store/, ''))} className="no-underline">
                                    <li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">
                                        {app.id === 'pomodoro' ? t('footer.pomodoro', app.name) : app.id === 'typewriting' ? t('footer.typewriting', app.name) : app.name}
                                    </li>
                                </a>
                            ))}
                            <a href={getAppsUrl()} className="no-underline">
                                <li className="text-[0.95rem] text-text opacity-80 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:opacity-100 hover:text-primary-cyan hover:pl-[1px]">
                                    {t('footer.allApps', 'All Apps')}
                                </li>
                            </a>
                        </ul>
                    </div>
                </div>
            </div>

            <hr className="h-px w-[96%] mx-auto bg-slider border-0 opacity-50" />

            <div className="flex items-center justify-between w-full px-10 py-6 box-border max-[820px]:flex-col max-[820px]:items-start max-[820px]:gap-5 max-[640px]:px-6">
                {/* Left: Version badge + Copyright */}
                <div className="flex flex-col gap-2 items-start">
                    <div className="h-fit w-fit rounded-[6px] px-[10px] py-1 bg-gray-666 shadow-sm">
                        <p className="text-white m-0 text-[0.75rem] font-semibold tracking-wide">v 8.0.0</p>
                    </div>
                    <div className="text-[0.9rem] text-text opacity-75 font-medium">&copy; 2026 Hari Vishnu</div>
                </div>

                {/* Right: Location & Language Switcher Buttons */}
                <div className="flex items-center gap-3 max-[640px]:w-full max-[640px]:flex-wrap">
                    {/* Location Chooser Button */}
                    <button
                        type="button"
                        onClick={() => setIsLocationModalOpen(true)}
                        className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-slider bg-hover text-text text-[0.85rem] font-medium transition-all duration-300 hover:border-primary-cyan hover:bg-hover/80 hover:-translate-y-0.5 cursor-pointer shadow-sm group"
                        title={t('common.selectLocation', 'Select Location')}
                    >
                        <span className="text-base leading-none group-hover:scale-110 transition-transform">{currentFlag}</span>
                        <span className="font-semibold text-text">{currentRegionName}</span>
                        <span className="text-xs opacity-65 font-mono px-1.5 py-0.5 rounded bg-slider/50">
                            {`${currentCurrencySymbol} ${currentCurrencyCode}`}
                        </span>
                        <i className="fi fi-rr-angle-small-down text-[12px] opacity-60 group-hover:opacity-100 flex items-center"></i>
                    </button>

                    {/* Language Chooser Button */}
                    <button
                        type="button"
                        onClick={() => setIsLanguageModalOpen(true)}
                        className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-slider bg-hover text-text text-[0.85rem] font-medium transition-all duration-300 hover:border-primary-cyan hover:bg-hover/80 hover:-translate-y-0.5 cursor-pointer shadow-sm group"
                        title={t('common.selectLanguage', 'Select Language')}
                    >
                        <span className="text-base leading-none group-hover:scale-110 transition-transform">{currentLanguage.flag}</span>
                        <span className="font-semibold text-text">{currentLanguage.nativeName}</span>
                        <i className="fi fi-rr-angle-small-down text-[12px] opacity-60 group-hover:opacity-100 flex items-center"></i>
                    </button>
                </div>
            </div>

            {/* Modals */}
            <FooterLocationModal
                isOpen={isLocationModalOpen}
                onClose={() => setIsLocationModalOpen(false)}
            />
            <FooterLanguageModal
                isOpen={isLanguageModalOpen}
                onClose={() => setIsLanguageModalOpen(false)}
            />
        </div>
    )
}
